import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXL } from '@sorare/core/src/atoms/typography';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import type { HexColorOnly } from '@sorare/core/src/style/types';

import { RankingInfo } from '../RankingInfo';
import { RewardBase } from '../RewardBase';
import type {
  MonetaryReward_anyRewardConfigInterface,
  MonetaryReward_so5Reward,
} from './__generated__/index.graphql';
import { RewardBaseAnimation } from '../RewardBase/RewardBaseAnimation';
import { RewardBaseIcon } from '../RewardBase/RewardBaseIcon';

const Root = styled.div`
  isolation: isolate;
  position: relative;
  transform-style: preserve-3d;
  aspect-ratio: 210 / 340;
`;
const Foreground = styled.div`
  width: 100%;
  height: 100%;

  transform: translateZ(-1px);

  .revealed & {
    transform: rotateY(180deg) translateZ(0px);
    opacity: 0;
  }
  transition:
    0.4s transform ease-in-out,
    0.4s opacity ease-in-out;
`;
const Background = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  transform: rotateY(180deg) translateZ(1px);
  opacity: 0;

  .revealed & {
    transform: rotateY(360deg) translateZ(1px);
    opacity: 1;
  }
  transition:
    0.4s transform ease-in-out,
    0.4s opacity ease-in-out;
`;
const IconWrapper = styled(Vertical).attrs({ center: true })`
  & > :first-child {
    z-index: -1;
    margin-bottom: calc(var(--double-unit) * -1);
  }
`;

type Props = {
  so5Reward: MonetaryReward_so5Reward;
  monetaryRewardConfig: MonetaryReward_anyRewardConfigInterface;
  leaguePictureUrl: string;
  onReveal?: () => void;
  revealed?: boolean;
};
export const MonetaryReward = ({
  so5Reward,
  monetaryRewardConfig,
  leaguePictureUrl,
  onReveal,
  revealed,
}: Props) => {
  const { so5Ranking, so5Leaderboard } = so5Reward;

  const props = {
    color: '#974DFF' as HexColorOnly,
    borderColor: `linear-gradient(to bottom, #974DFF, var(--c-black) 8%, var(--c-black) 92%, #974DFF)`,
  };

  return (
    <Root className={classNames({ revealed })}>
      <Background>
        <RewardBase
          {...props}
          background="linear-gradient(to right, var(--c-nl-500), var(--c-nl-500)), conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 25%, var(--c-black) 0) 0 0/5px 5px, var(--c-black)"
          topRightIcon={
            !!so5Leaderboard.iconUrl && (
              <img height={40} src={so5Leaderboard.iconUrl} alt="" />
            )
          }
          icon={
            <RewardBaseAnimation revealed={revealed}>
              <IconWrapper>
                <RewardBaseIcon>
                  <RewardConfigImage
                    size={10}
                    anyRewardConfig={monetaryRewardConfig}
                  />
                </RewardBaseIcon>
                <BrandXL>
                  <RewardConfigText anyRewardConfig={monetaryRewardConfig} />
                </BrandXL>
              </IconWrapper>
            </RewardBaseAnimation>
          }
          body={
            <RewardBaseAnimation revealed={revealed}>
              <RankingInfo so5Ranking={so5Ranking} />
            </RewardBaseAnimation>
          }
          leaguePictureUrl={leaguePictureUrl}
          revealed
        />
      </Background>
      <Foreground>
        <RewardBase
          {...props}
          background="linear-gradient(to right, var(--c-nl-500), var(--c-nl-500)), conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 25%, var(--c-black) 0) 0 0/5px 5px, var(--c-black)"
          icon={
            !!monetaryRewardConfig && (
              <RewardBaseIcon>
                <RewardConfigImage
                  size={10}
                  anyRewardConfig={monetaryRewardConfig}
                />
              </RewardBaseIcon>
            )
          }
          leaguePictureUrl={leaguePictureUrl}
          onReveal={onReveal}
        />
      </Foreground>
    </Root>
  );
};

MonetaryReward.fragments = {
  anyRewardConfigInterface: gql`
    fragment MonetaryReward_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ...RewardConfigImage_anyRewardConfigInterface
      ...RewardConfigText_anyRewardConfigInterface
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<MonetaryReward_anyRewardConfigInterface>,
  so5Reward: gql`
    fragment MonetaryReward_so5Reward on So5Reward {
      slug
      id
      so5Leaderboard {
        slug
        iconUrl
      }
      so5Ranking {
        id
        ...RankingInfo_so5Ranking
      }
    }
    ${RankingInfo.fragments.so5Ranking}
  ` as TypedDocumentNode<MonetaryReward_so5Reward>,
};
